import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { BookMarked, Plus, Settings, Sparkles, UserRound, type LucideIcon } from "lucide-react";
import { useLingui } from "@lingui/react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { UserMenu } from "@/components/layout/UserMenu";
import { useOrganisation } from "@/hooks/settings/useOrganisation";
import { useImageUrl } from "@/hooks/useImage";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useAccess } from "@/hooks/useAccess";
import { canUseMePortal } from "@/components/me/mePortal";
import { useWhatsNewUnseen } from "@/guide/whatsNew";
import { ROUTE_MODULES, routeModules, type RouteAccess } from "@/lib/modules";
import { NAV_SECTIONS, isUnderPath, matchNavItem, navItemLabel, type NavBadgeKey } from "@/lib/navigation";
import { isDirectImageUrl, isImageId } from "@/utils/imageUtils";

/** The access rule of a sidebar link; a link without a route entry is admin-only. */
function routeAccessOf(href: string): RouteAccess {
  return ROUTE_MODULES[href] ?? 'admin';
}

/** A link in the footer's icon row. */
interface FooterLink {
  href: string;
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  /** Screen-reader text of the dot on the icon; no dot when undefined. */
  dot?: string;
}

/** A count as the badge shows it: nothing at zero, "99+" above 99. */
function badgeText(count: number | undefined): string | undefined {
  if (!count || count <= 0) return undefined;
  return count > 99 ? '99+' : String(count);
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const organisation = useOrganisation().data;

  // The organisation logo is an image id (resolved to a URL) or a direct URL.
  const logoValue = organisation?.logo_url;
  const needsImageUrlResolution = isImageId(logoValue);
  const resolvedImageUrl = useImageUrl(needsImageUrlResolution ? logoValue : null);
  let logo_url: string | undefined;
  if (needsImageUrlResolution && resolvedImageUrl) {
    logo_url = resolvedImageUrl;
  } else if (isDirectImageUrl(logoValue)) {
    logo_url = logoValue;
  }

  // Module filter (issue #1670): an employee sees only the items of the modules
  // their role holds, so no employee lands on a 403 page. An admin sees every
  // item. The server module gates are the boundary; this is UX.
  const { isAdmin, hasModule } = useAccess();
  const canSee = React.useCallback(
    (href: string) => {
      const access = routeAccessOf(href);
      return isAdmin || (access !== 'admin' && routeModules(access).some(hasModule));
    },
    [isAdmin, hasModule],
  );

  // Badge counts run only when the session may open the page (module gated in the hook).
  const navBadges = useNavBadges();
  const badges: Partial<Record<NavBadgeKey, string | undefined>> = useMemo(
    () => ({
      approvals: badgeText(navBadges.approvals.count),
      messages: badgeText(navBadges.messages.count),
      absences: badgeText(navBadges.absences.count),
      timeEntries: badgeText(navBadges.timeEntries.count),
      overdueInvoices: badgeText(navBadges.overdueInvoices.count),
    }),
    [navBadges],
  );

  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => canSee(item.href)),
      })).filter((section) => section.items.length > 0),
    [canSee],
  );

  const showMyAccount = canUseMePortal({ isAdmin, hasModule });
  // The longest matching item only: /children/consents lies under /children too.
  const activeHref = matchNavItem(location.pathname)?.item.href;
  const onSettings = isUnderPath(location.pathname, '/settings');
  const settingsLabel = i18n._({ id: 'settings.title', message: 'Settings' });
  const myAccountLabel = i18n._({ id: 'mePortal.userMenu.myAccount', message: 'My account' });
  const guideLabel = i18n._({ id: 'guide.title', message: 'Guide' });
  const onGuide = isUnderPath(location.pathname, '/guide');
  const whatsNewLabel = i18n._({ id: 'whatsNew.title', message: "What's new" });
  const onWhatsNew = isUnderPath(location.pathname, '/whats-new');
  const whatsNewUnseen = useWhatsNewUnseen();
  const { state: sidebarState } = useSidebar();

  const footerLinks: FooterLink[] = [
    // Settings are admin-only (issue #1670).
    ...(isAdmin ? [{ href: '/settings', label: settingsLabel, icon: Settings, isActive: onSettings }] : []),
    // "My account" opens the /me self-service portal (#1768): own shifts, absences, hours.
    ...(showMyAccount ? [{ href: '/me', label: myAccountLabel, icon: UserRound, isActive: isUnderPath(location.pathname, '/me') }] : []),
    // The platform guide (#2059): every user, each sees the topics of their own areas.
    { href: '/guide', label: guideLabel, icon: BookMarked, isActive: onGuide },
    // What's new (#2109): a dot until this browser opened the latest release notes.
    {
      href: '/whats-new',
      label: whatsNewLabel,
      icon: Sparkles,
      isActive: onWhatsNew,
      dot: whatsNewUnseen ? i18n._({ id: 'whatsNew.badge.unseen', message: 'New release notes' }) : undefined,
    },
  ];

  return (
    <Sidebar variant="inset" collapsible="icon" className="!border-r-0">
      <SidebarHeader className="p-4 [@media(max-height:820px)]:p-3">
        {/* The logo goes to "/": the route guard sends an employee on to the first granted module. */}
        <Link to="/" className="flex items-center space-x-2 group-data-[collapsible=icon]:justify-center no-underline">
          <Avatar className="h-8 w-8 flex-shrink-0">
            {logo_url && <AvatarImage src={logo_url} alt="" />}
            <AvatarFallback className="bg-primary text-primary-foreground font-bold">
              {organisation?.name?.split(' ').map((word) => word[0]).join('').toUpperCase() || 'ORG'}
            </AvatarFallback>
          </Avatar>
          <span className="group-data-[collapsible=icon]:hidden truncate font-semibold text-sm text-sidebar-foreground">
            {organisation?.name || i18n._({ id: 'nav.organizationDefault', message: 'Organization' })}
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {sections.map((section) => (
          <SidebarGroup key={section.key} className="[@media(max-height:820px)]:py-1">
            {section.label && (
              <SidebarGroupLabel className="text-sidebar-foreground/70 [@media(max-height:820px)]:h-6">
                {i18n._(section.label)}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const title = i18n._(navItemLabel(item));
                  const isActive = item.href === activeHref;
                  const badge = item.badge ? badges[item.badge] : undefined;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={title} className="[@media(max-height:820px)]:h-7">
                        <Link to={item.href} title={title} aria-current={isActive ? 'page' : undefined} className="relative">
                          <item.icon />
                          <span className="truncate">{title}</span>
                          {badge && (
                            <>
                              <span
                                className="ml-auto rounded-full bg-destructive px-1.5 py-0.5 text-center text-xs font-medium text-destructive-foreground min-w-[1.25rem] group-data-[collapsible=icon]:hidden"
                                aria-hidden="true"
                                data-testid={item.href === '/messaging' ? 'sidebar-unread-badge' : undefined}
                              >
                                {badge}
                              </span>
                              {/* Collapsed rail: a dot keeps the signal without the number. */}
                              <span
                                className="absolute right-1 top-1 hidden h-2 w-2 rounded-full bg-destructive group-data-[collapsible=icon]:block"
                                aria-hidden="true"
                              />
                              <span className="sr-only">
                                {i18n._({ id: 'nav.badge.openItems', message: '{count} open' }, { count: badge })}
                              </span>
                            </>
                          )}
                        </Link>
                      </SidebarMenuButton>
                      {item.addHref && (
                        <SidebarMenuAction
                          className="peer-data-[active=true]/menu-button:opacity-100"
                          onClick={() => navigate(item.addHref as string)}
                        >
                          <Plus />
                          <span className="sr-only">
                            {i18n._({ id: 'nav.addItem', message: 'Add: {title}' }, { title })}
                          </span>
                        </SidebarMenuAction>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="gap-1 py-3">
        {/* One icon row instead of four full rows: the nav above keeps the height.
            The label shows as a tooltip on hover and keyboard focus. */}
        <nav aria-label={i18n._({ id: 'nav.footerLinks', message: 'Platform' })}>
          <ul className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
            {footerLinks.map((link) => (
              <li key={link.href} className="flex-1 group-data-[collapsible=icon]:flex-none">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={link.href}
                      aria-current={link.isActive ? 'page' : undefined}
                      data-active={link.isActive}
                      className="relative flex h-8 w-full items-center justify-center rounded-md text-sidebar-foreground outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:w-8 [&>svg]:size-4 [&>svg]:shrink-0"
                    >
                      <link.icon aria-hidden="true" />
                      <span className="sr-only">{link.label}</span>
                      {link.dot && (
                        <>
                          <span
                            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary"
                            aria-hidden="true"
                            data-testid="whats-new-dot"
                          />
                          <span className="sr-only">{link.dot}</span>
                        </>
                      )}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side={sidebarState === 'collapsed' ? 'right' : 'top'}>{link.label}</TooltipContent>
                </Tooltip>
              </li>
            ))}
          </ul>
        </nav>
        <SidebarMenu>
          <SidebarMenuItem>
            <UserMenu showMyAccount={showMyAccount} showNotifications={isAdmin} />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
